import { useCallback } from 'react';

import { useHubTheming } from '../../controllers/hub-theming-context';
import { resolveHubAutoTextColor, type HubAutoTextColorArgs } from './resolve-hub-auto-text-color';
import { useIsHubAutoTextColorEnabled } from './use-is-hub-auto-text-color-enabled';

/**
 * Returns a resolver that keeps black and white element text legible against
 * whatever the background currently resolves to.
 *
 * ```ts
 * const resolveTextColor = useHubAutoTextColor('cards-extension');
 * const textColor = resolveTextColor({ backgroundColor, textColor: stored, defaultRef });
 * ```
 *
 * A resolver rather than a value, so the same call shape works for an element
 * with one surface and for one that maps over several — the carousel derives a
 * colour per frame, which a value-returning hook could not do.
 *
 * Two things this fixes, both of which the old `useAutoTransitioningColors`
 * got wrong:
 *
 *  1. **When it runs.** That hook was a `useEffect` keyed on `[backgroundColor]`
 *     and mounted only inside `*-config-panel*` components, so a brand-colour
 *     change could not reach an element whose panel was closed — the admin had
 *     to open each element in turn for its text to catch up. Deriving during
 *     render means the provider re-render that follows a brand change suffices.
 *  2. **What it measures.** Readability was evaluated against the raw ADF
 *     string, and for a hub var that resolves to `''` on
 *     `document.documentElement` — where the hub palette does not live — which
 *     `tinycolor` reads as black, inverting the verdict on light backgrounds.
 *     The background is now resolved through the in-memory palette.
 *
 * Works on both tiers: an un-entitled (Free / Standard) hub paints every
 * element with the brand colour itself, so the unstored-background default is
 * resolved per tier rather than always through the palette shade — see
 * `resolveHubAutoTextColor`.
 *
 * Nothing is written to the ADF: this is display-time only, so view mode stays
 * read-only and a stored value is never silently rewritten. Values other than
 * black/white are returned untouched — an admin who picked a brand shade or
 * typed a hex keeps it.
 *
 * Active for JSM Help Hubs with advanced theming enabled. Confluence Company
 * Hub and Customer Service hubs retain their existing text colours.
 */
export const useHubAutoTextColor = (
	packageName: string,
): ((args: HubAutoTextColorArgs) => string | undefined) => {
	const theming = useHubTheming();
	const isAutoTextColorEnabled = useIsHubAutoTextColorEnabled();

	const isEnabled = Boolean(
		isAutoTextColorEnabled && theming?.isAdvancedThemingEnabled && theming.palette,
	);
	const palette = theming?.palette;
	const primaryColor = theming?.primaryColor;
	const isBrandPaletteLocked = theming?.isBrandPaletteLocked;

	return useCallback(
		({ backgroundColor, textColor, defaultRef }: HubAutoTextColorArgs) => {
			if (!isEnabled || !palette || !primaryColor) {
				return textColor;
			}

			return resolveHubAutoTextColor({
				backgroundColor,
				textColor,
				defaultRef,
				palette,
				primaryColor,
				isBrandPaletteLocked,
				packageName,
			});
		},
		[isEnabled, palette, primaryColor, isBrandPaletteLocked, packageName],
	);
};
